import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, User, ChefHat, Truck, Package, ShoppingCart, Info, Edit, ArrowRightLeft, UserCheck, UserX, Trash2, X } from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'
import { Store } from '../../../types'

interface EmployeeDetails {
  id: string
  name: string
  email: string
  phone: string
  type: 'store_admin' | 'dispatcher' | 'chef' | 'delivery'
  is_active: boolean
  created_at: string
  last_login_at?: string
  store_id: string
  store?: {
    id: string
    name: string
  }
  stats?: {
    totalOrders: number
    completedOrders: number
    activeOrders: number
  }
}

interface Order {
  id: string
  customer_name: string
  customer_phone: string
  customer_email: string
  total_amount: string
  delivery_fee: string
  status: string
  created_at: string
  updated_at: string
  store_name: string
}

const EmployeeDetails = () => {
  const navigate = useNavigate()
  const { employeeId } = useParams<{ employeeId: string }>()
  const { activeStore, stores } = useStore()
  const [employee, setEmployee] = useState<EmployeeDetails | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [showReassignModal, setShowReassignModal] = useState(false)
  const [reassignStoreId, setReassignStoreId] = useState<string>('')

  useEffect(() => {
    if (employeeId) {
      loadEmployeeDetails()
      loadEmployeeOrders()
    }
  }, [employeeId])

  const loadEmployeeDetails = async () => {
    if (!employeeId) return

    const cacheKey = `employee_details_${employeeId}`
    
    // Intentar cargar desde cache primero
    const cachedEmployee = getCacheStale<EmployeeDetails>(cacheKey)
    if (cachedEmployee) {
      setEmployee(cachedEmployee)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/auth/my-store/employees/${employeeId}`)
      
      if (response.data?.success) {
        setEmployee(response.data.data)
        
        // Guardar en cache (10 minutos de TTL)
        setCache(cacheKey, response.data.data, 10 * 60 * 1000)
      } else {
        toast.error('Error al cargar detalles del empleado')
        navigate(-1)
      }
    } catch (error: any) {
      console.error('Error loading employee details:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedEmployee) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error(error.response?.data?.message || 'Error al cargar detalles del empleado')
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }

  const loadEmployeeOrders = async () => {
    if (!employeeId) return

    const cacheKey = `employee_orders_${employeeId}`
    
    // Intentar cargar desde cache primero
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setOrders(cachedOrders)
      setLoadingOrders(false)
    }

    try {
      setLoadingOrders(true)
      const response = await api.get(`/auth/my-store/employees/${employeeId}/orders?limit=20`)
      
      if (response.data?.success) {
        const ordersData = response.data.data || []
        setOrders(ordersData)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, ordersData, 5 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading employee orders:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // No mostrar error, solo no cargar pedidos
    } finally {
      setLoadingOrders(false)
    }
  }

  const handleToggleStatus = async (isActive: boolean) => {
    if (!employeeId) return

    try {
      const response = await api.patch(`/auth/my-store/employees/${employeeId}`, { isActive })
      if (response.data?.success) {
        toast.success(isActive ? 'Empleado activado' : 'Empleado desactivado')
        loadEmployeeDetails()
      }
    } catch (error: any) {
      console.error('Error updating employee status:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar estado del empleado')
    }
  }

  const handleReassignEmployee = async () => {
    if (!employeeId || !reassignStoreId) {
      toast.error('Debes seleccionar un negocio')
      return
    }

    if (employee && reassignStoreId === employee.store_id) {
      toast.error('El empleado ya está asignado a este negocio')
      return
    }

    try {
      const response = await api.patch(`/auth/my-store/employees/${employeeId}/reassign`, {
        store_id: reassignStoreId
      })
      
      if (response.data?.success) {
        toast.success(response.data.message || 'Empleado reasignado exitosamente')
        setShowReassignModal(false)
        setReassignStoreId('')
        loadEmployeeDetails()
      }
    } catch (error: any) {
      console.error('Error reassigning employee:', error)
      const errorMessage = error.response?.data?.message || 'Error al reasignar empleado'
      toast.error(errorMessage)
      
      if (error.response?.status === 400 && errorMessage.includes('ya está asignado')) {
        setShowReassignModal(false)
        setReassignStoreId('')
      }
    }
  }

  const handleDeleteEmployee = async () => {
    if (!employeeId) return

    if (!confirm('¿Estás seguro de que quieres ELIMINAR permanentemente este empleado? Esta acción no se puede deshacer.')) return

    try {
      const response = await api.delete(`/auth/my-store/employees/${employeeId}`)
      if (response.data?.success) {
        toast.success(response.data.message || 'Empleado eliminado exitosamente')
        navigate('/store/' + (employee?.store_id || activeStore?.id) + '/employees')
      }
    } catch (error: any) {
      console.error('Error deleting employee:', error)
      const errorMessage = error.response?.data?.message || 'Error al eliminar empleado'
      toast.error(errorMessage)
      
      if (error.response?.status === 400 && errorMessage.includes('pedido')) {
        toast.error('Primero desactiva al empleado o espera a que complete sus pedidos activos', { duration: 5000 })
      }
    }
  }

  const getRoleIcon = (type: EmployeeDetails['type']) => {
    switch (type) {
      case 'store_admin': return <User className="w-6 h-6" />
      case 'dispatcher': return <Package className="w-6 h-6" />
      case 'chef': return <ChefHat className="w-6 h-6" />
      case 'delivery': return <Truck className="w-6 h-6" />
      default: return <User className="w-6 h-6" />
    }
  }

  const getRoleColor = (type: EmployeeDetails['type']) => {
    switch (type) {
      case 'store_admin': return 'bg-purple-100 text-purple-800'
      case 'dispatcher': return 'bg-blue-100 text-blue-800'
      case 'chef': return 'bg-orange-100 text-orange-800'
      case 'delivery': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleLabel = (type: EmployeeDetails['type']) => {
    switch (type) {
      case 'store_admin': return 'Administrador'
      case 'dispatcher': return 'Despachador'
      case 'chef': return 'Cocinero'
      case 'delivery': return 'Repartidor'
      default: return type
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-700'
      case 'cancelled': return 'bg-red-100 text-red-700'
      case 'pending':
      case 'confirmed':
      case 'preparing':
      case 'ready':
      case 'delivering': return 'bg-yellow-100 text-yellow-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      preparing: 'Preparando',
      ready: 'Listo',
      delivering: 'En camino',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    }
    return statusMap[status] || status
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando detalles del empleado...</p>
        </div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gray-100 mb-6">
            <Info className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Empleado no encontrado</h2>
          <p className="text-gray-500 mb-8">No se pudo cargar la información del empleado</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all duration-200 active:scale-95 font-medium shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
          >
            Volver
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Header - Modern iMac Style */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-2xl border-b border-gray-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 -ml-2.5 rounded-xl hover:bg-gray-100/60 transition-all duration-200 active:scale-95"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1 text-center px-4">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
                Detalles del Empleado
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium truncate">
                {employee.name}
              </p>
            </div>
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-area-bottom">
        {/* Employee Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200/80 p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl flex items-center justify-center shadow-lg flex-shrink-0">
              <div className="text-white">
                {getRoleIcon(employee.type)}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">{employee.name}</h2>
              <p className="text-gray-600 mb-3 truncate">{employee.email}</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold border ${getRoleColor(employee.type)} border-current/20`}>
                  {getRoleLabel(employee.type)}
                </span>
                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                  employee.is_active
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {employee.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate(`/store/${employee.store_id || activeStore?.id}/employees/${employee.id}/edit`)}
              className="flex flex-col items-center justify-center p-3 sm:p-4 bg-blue-50 rounded-xl text-xs sm:text-sm font-medium text-blue-700 hover:bg-blue-100 transition-all duration-200 active:scale-95 border border-blue-100"
            >
              <Edit className="w-5 h-5 mb-1.5" />
              <span>Editar</span>
            </button>

            {employee.is_active ? (
              <button
                onClick={() => handleToggleStatus(false)}
                className="flex flex-col items-center justify-center p-3 sm:p-4 bg-yellow-50 rounded-xl text-xs sm:text-sm font-medium text-yellow-700 hover:bg-yellow-100 transition-all duration-200 active:scale-95 border border-yellow-100"
              >
                <UserX className="w-5 h-5 mb-1.5" />
                <span>Desactivar</span>
              </button>
            ) : (
              <button
                onClick={() => handleToggleStatus(true)}
                className="flex flex-col items-center justify-center p-3 sm:p-4 bg-green-50 rounded-xl text-xs sm:text-sm font-medium text-green-700 hover:bg-green-100 transition-all duration-200 active:scale-95 border border-green-100"
              >
                <UserCheck className="w-5 h-5 mb-1.5" />
                <span>Activar</span>
              </button>
            )}

            <button
              onClick={() => setShowReassignModal(true)}
              className="flex flex-col items-center justify-center p-3 sm:p-4 bg-indigo-50 rounded-xl text-xs sm:text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-all duration-200 active:scale-95 border border-indigo-100"
            >
              <ArrowRightLeft className="w-5 h-5 mb-1.5" />
              <span>Reasignar</span>
            </button>

            <button
              onClick={handleDeleteEmployee}
              className="flex flex-col items-center justify-center p-3 sm:p-4 bg-red-50 rounded-xl text-xs sm:text-sm font-medium text-red-700 hover:bg-red-100 transition-all duration-200 active:scale-95 border border-red-100"
            >
              <Trash2 className="w-5 h-5 mb-1.5" />
              <span>Eliminar</span>
            </button>
          </div>
        </div>

        {/* Employee Information */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200/80 p-6 sm:p-8 mb-6">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Información Personal</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Nombre Completo</label>
              <p className="text-lg font-medium text-gray-900">{employee.name}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Correo Electrónico</label>
              <p className="text-lg font-medium text-gray-900 break-all">{employee.email}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Teléfono</label>
              <p className="text-lg font-medium text-gray-900">{employee.phone || 'N/A'}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Rol</label>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold border ${getRoleColor(employee.type)} border-current/20`}>
                {getRoleLabel(employee.type)}
              </span>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Estado</label>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold border ${
                employee.is_active
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}>
                {employee.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
            {employee.store && (
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Negocio</label>
                <p className="text-lg font-medium text-gray-900">{employee.store.name}</p>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Fecha de Registro</label>
              <p className="text-lg font-medium text-gray-900">
                {new Date(employee.created_at).toLocaleDateString('es-DO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            {employee.last_login_at && (
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Último Acceso</label>
                <p className="text-lg font-medium text-gray-900">
                  {new Date(employee.last_login_at).toLocaleDateString('es-DO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        {employee.stats && (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-3xl shadow-sm border border-blue-200/50 p-6 sm:p-8 mb-6">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Estadísticas (Últimos 30 días)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center bg-white rounded-2xl p-6 shadow-sm border border-gray-200/50">
                <div className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">{employee.stats.totalOrders}</div>
                <div className="text-sm font-medium text-gray-600">Pedidos Totales</div>
              </div>
              <div className="text-center bg-white rounded-2xl p-6 shadow-sm border border-gray-200/50">
                <div className="text-3xl sm:text-4xl font-bold text-green-600 mb-2">{employee.stats.completedOrders}</div>
                <div className="text-sm font-medium text-gray-600">Completados</div>
              </div>
              <div className="text-center bg-white rounded-2xl p-6 shadow-sm border border-gray-200/50">
                <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-2">{employee.stats.activeOrders}</div>
                <div className="text-sm font-medium text-gray-600">Activos</div>
              </div>
            </div>
          </div>
        )}

        {/* Orders */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200/80 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">Pedidos Asignados</h3>
            {loadingOrders && (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent"></div>
            )}
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gray-100 mb-6">
                <ShoppingCart className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-xl font-semibold text-gray-900 mb-2">No hay pedidos asignados</p>
              <p className="text-gray-500">Este empleado no tiene pedidos asignados aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div key={order.id} className="bg-gray-50 border border-gray-200 rounded-2xl p-4 sm:p-5 hover:bg-gray-100 transition-all duration-200 hover:shadow-sm">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900 text-lg">{order.customer_name}</h4>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${getStatusColor(order.status)} border-current/20`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{order.store_name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('es-DO', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        RD$ {parseFloat(order.total_amount).toLocaleString()}
                      </p>
                      {parseFloat(order.delivery_fee) > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          + RD$ {parseFloat(order.delivery_fee).toLocaleString()} entrega
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full my-8 border border-gray-200/50">
            <div className="p-6 sm:p-8 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">Reasignar Empleado</h2>
                  <p className="text-sm text-gray-500">Mueve el empleado a otro negocio</p>
                </div>
                <button
                  onClick={() => {
                    setShowReassignModal(false)
                    setReassignStoreId('')
                  }}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-5">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Reasignar <strong className="text-gray-900">{employee.name}</strong> a otro negocio:
                </p>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Seleccionar Negocio
                </label>
                <select
                  value={reassignStoreId}
                  onChange={(e) => setReassignStoreId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                >
                  <option value="">Selecciona un negocio</option>
                  {stores
                    .filter((store: Store) => store.id !== employee.store_id)
                    .map((store: Store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                </select>
                {stores.find((s: Store) => s.id === employee.store_id) && (
                  <p className="text-xs text-gray-500 mt-2 font-medium">
                    Actualmente asignado a: <strong className="text-gray-700">{stores.find((s: Store) => s.id === employee.store_id)?.name}</strong>
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowReassignModal(false)
                    setReassignStoreId('')
                  }}
                  className="flex-1 px-4 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-200 border border-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReassignEmployee}
                  disabled={!reassignStoreId}
                  className="flex-1 px-4 py-3.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
                >
                  Reasignar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeDetails

